import {
  ChangeDetectionStrategy, Component, EventEmitter,
  Input, OnChanges, Output, SimpleChanges, signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MeetingDetailTableComponent } from '../../../../shared/components/meeting-detail-table/meeting-detail-table.component';
import { TimezoneSelectComponent } from '../../../../shared/components/timezone-select/timezone-select.component';
import { CancelDetails } from '../../models/pcheck.model';
import { browserTimezone, isIsoUtc } from '../../helpers/booking-timezone';

const CONFIRM_PHRASE = 'CANCEL';

@Component({
  selector: 'app-cancel-card',
  standalone: true,
  imports: [CommonModule, MeetingDetailTableComponent, TimezoneSelectComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './cancel-card.component.html',
  styleUrls: ['./cancel-card.component.scss'],
})
export class CancelCardComponent implements OnChanges {
  @Input() details!: CancelDetails;
  @Input() confirmed = false;
  @Input() dismissed = false;

  @Output() cancelConfirmed = new EventEmitter<{ reason: string }>();
  @Output() cancelDismissed = new EventEmitter<void>();

  readonly reason      = signal('');
  readonly typedPhrase = signal('');
  // Default to the timezone the user selected when booking (PST by default in
  // the slot picker), falling back to the browser's local zone for legacy
  // meetings with no recorded timezone. The user can still switch zones.
  readonly displayTz   = signal(browserTimezone());

  /** Show the timezone picker only for UTC ISO times (regular pcheck). */
  get showTzPicker(): boolean { return isIsoUtc(this.details?.start_time); }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['details'] && this.details?.timezone) {
      this.displayTz.set(this.details.timezone);
    }
  }

  onTimezoneChange(tz: string): void { this.displayTz.set(tz); }

  get canSubmit(): boolean {
    // Phrase must be typed exactly as shown — uppercase only — to prevent
    // accidental confirmation. Do NOT normalise case here.
    return this.reason().trim().length > 0 &&
           this.typedPhrase().trim() === CONFIRM_PHRASE;
  }

  onReasonInput(event: Event): void {
    this.reason.set((event.target as HTMLTextAreaElement).value);
  }

  onTypedInput(event: Event): void {
    this.typedPhrase.set((event.target as HTMLInputElement).value);
  }

  onConfirm(): void {
    if (this.canSubmit) this.cancelConfirmed.emit({ reason: this.reason() });
  }

  onDismiss(): void { this.cancelDismissed.emit(); }
}
